import React, { useEffect, useRef, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Loader } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '@/config';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useContext(AuthContext);
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.slice(1)); // Remove '#' from hash
        const sessionId = params.get('session_id');
        
        if (!sessionId) {
          console.error('No session_id found in URL');
          setError('No se encontró session_id en la URL');
          toast.error('Error: No se encontró la sesión de Google');
          navigate('/auth/login');
          return;
        }
        
        // Exchange session_id for user data
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        });
        
        const { session_token, user } = response.data;
        
        if (!session_token) {
          setError('No se recibió token de sesión');
          toast.error('Error: No se recibió token de sesión');
          navigate('/auth/login');
          return;
        }
        
        // Store token and user
        localStorage.setItem('access_token', session_token);
        setToken(session_token);
        setUser(user);
        
        toast.success('¡Bienvenido a nasca360.com!');
        // Redirect to gallery with user data
        navigate('/gallery', { state: { user }, replace: true });
        
      } catch (error) {
        console.error('Auth callback error:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
        setError(errorMsg);
        toast.error(`Error de autenticación: ${errorMsg}`);
        // Small delay so user can see the error message
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      }
    };
    
    processSession();
  }, [location, navigate, setUser, setToken]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center px-4">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-16 h-16 text-red-500 mx-auto mb-4 flex items-center justify-center text-4xl">!</div>
            <h2 className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Playfair Display, serif' }}>
              Error de Autenticación
            </h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <p className="text-gray-400 mt-1 text-sm">Redirigiendo al login...</p>
          </>
        ) : (
          <>
            <Loader className="w-16 h-16 text-amber-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Autenticando...
            </h2>
            <p className="text-gray-600 mt-2">Procesando tu inicio de sesión con Google</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
